import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { WEB_URL } from "@/lib/webUrl";
import { routes, sortCzech } from "@ckgo2/shared";
import {
  BLOG_RELATED_TRIPS_MAX,
  blogPostCreateSchema,
  type BlogPostCreateInput,
} from "@ckgo2/shared/schemas";
import { BLOG_CATEGORIES, BLOG_STATUSES } from "@ckgo2/shared/types";
import type { BlogPost, Trip } from "@ckgo2/shared/types";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import MediaPicker from "@/components/common/MediaPicker";
import RichTextEditor from "@/components/editor/RichTextEditor";
import EditPageHeader from "@/components/common/EditPageHeader";
import { useEditPage } from "@/hooks/useEditPage";
import DestinationPicker from "@/components/common/DestinationPicker";

export default function BlogPostEditPage() {
  const { t } = useTranslation();

  const categoryLabels: Record<string, string> = {
    "travel-tips": t("content.blog.categories.travelTips"),
    culture: t("content.blog.categories.culture"),
    food: t("content.blog.categories.food"),
    nature: t("content.blog.categories.nature"),
    guides: t("content.blog.categories.guides"),
    news: t("content.blog.categories.news"),
  };

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
    archived: t("common.status.archived"),
  };

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    BlogPostCreateInput,
    BlogPost
  >({
    resourceKey: "blogPost",
    listKey: "blogPosts",
    apiPath: "/api/admin/blog",
    schema: blogPostCreateSchema,
    defaultValues: {
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featuredImage: "",
      featuredImageId: null,
      galleryImages: [],
      galleryImageIds: [],
      category: "travel-tips",
      tags: [],
      destinationId: "",
      relatedTripIds: [],
      authorName: "CK Go2 Team",
      authorAvatar: "",
      authorAvatarId: null,
      readTime: null,
      isFeatured: false,
      status: "draft",
      publishedAt: null,
      sortOrder: 0,
      metaTitle: "",
      metaDescription: "",
    },
    navigateTo: "/blog",
    transformOnLoad: (data) => {
      const d = { ...data } as Record<string, unknown>;
      // Convert ISO publishedAt to datetime-local format
      if (d.publishedAt && typeof d.publishedAt === "string") {
        d.publishedAt = (d.publishedAt as string).slice(0, 16);
      }
      if (!d.destinationId) {
        d.destinationId = "";
      }
      return d as unknown as BlogPostCreateInput;
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Fetch trips for related trips checkboxes
  const { data: tripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  const allTrips = sortCzech(
    (tripsData?.data || []).filter((t) => t.status === "published"),
    (t) => t.title,
  );

  const relatedTripIds = watch("relatedTripIds") || [];
  // Mez ze schématu (validuje celé pole, i id zájezdu, který se mezitím odpublikoval
  // a v mřížce chybí), takže se počítá z pole formuláře, ne ze zaškrtnutých polí.
  const atRelatedLimit = relatedTripIds.length >= BLOG_RELATED_TRIPS_MAX;
  const toggleTripId = (tripId: string) => {
    const selected = relatedTripIds.includes(tripId);
    // Odebrat jde vždy; přidat nad mez ne (pojistka k `disabled` u pole).
    if (!selected && atRelatedLimit) return;
    const next = selected
      ? relatedTripIds.filter((id) => id !== tripId)
      : [...relatedTripIds, tripId];
    setValue("relatedTripIds", next, { shouldDirty: true });
  };
  // Vybraná id, která mřížka neukazuje (zájezd se mezitím odpublikoval nebo smazal).
  // Odškrtnout je nejde, a přitom se počítají do meze, proto je editor umí odebrat
  // naráz. Dokud se zájezdy nenačetly, skryté není nic (prázdný seznam by je dělal všechny).
  const visibleTripIds = new Set(allTrips.map((trip) => trip.id));
  const hiddenRelatedTripIds = tripsData
    ? relatedTripIds.filter((id) => !visibleTripIds.has(id))
    : [];
  const removeHiddenRelatedTrips = () => {
    setValue(
      "relatedTripIds",
      relatedTripIds.filter((id) => visibleTripIds.has(id)),
      { shouldDirty: true },
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/blog"
        isNew={isNew}
        newTitle={t("content.blog.form.newTitle")}
        editTitle={t("content.blog.form.editTitle")}
        entityName={itemData?.title}
        viewUrl={
          !isNew && watch("slug") ? `${WEB_URL}${routes.blogPost(watch("slug"))}` : undefined
        }
        formId="blog-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        validationErrors={errors}
        isSuccess={saveMutation.isSuccess}
      />

      <form id="blog-form" onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.blog.form.basicInfo")}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="title" className="label">
                  {t("common.fields.title")} *
                </label>
                <input
                  {...register("title")}
                  type="text"
                  id="title"
                  className={`input ${errors.title ? "input-error" : ""}`}
                  placeholder={t("content.blog.form.titlePlaceholder")}
                />
                {errors.title && (
                  <p className="text-error-600 mt-1 text-sm">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="slug" className="label">
                  {t("content.blog.form.slugLabel")} *
                </label>
                <input
                  {...register("slug")}
                  type="text"
                  id="slug"
                  className={`input ${errors.slug ? "input-error" : ""}`}
                  placeholder={t("content.blog.form.slugPlaceholder")}
                />
                {errors.slug && (
                  <p className="text-error-600 mt-1 text-sm">{errors.slug.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="category" className="label">
                  {t("content.blog.form.categoryLabel")} *
                </label>
                <select {...register("category")} id="category" className="input">
                  {BLOG_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {categoryLabels[cat] || cat}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-error-600 mt-1 text-sm">{errors.category.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="destinationId" className="label">
                  {t("common.fields.destination")}
                </label>
                <Controller
                  name="destinationId"
                  control={control}
                  render={({ field }) => (
                    <DestinationPicker
                      id="destinationId"
                      value={field.value}
                      onChange={(id) => field.onChange(id ?? "")}
                    />
                  )}
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="excerpt" className="label">
                {t("content.blog.form.excerptLabel")}
              </label>
              <textarea
                {...register("excerpt")}
                id="excerpt"
                rows={2}
                className="input"
                placeholder={t("content.blog.form.excerptPlaceholder")}
                maxLength={500}
              />
            </div>
          </div>

          {/* Content - TipTap */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.blog.form.contentTitle")}
            </h2>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder={t("content.blog.form.contentPlaceholder")}
                />
              )}
            />
          </div>

          {/* Gallery Images */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.blog.form.galleryTitle")}
            </h2>
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              <strong>{t("content.blog.form.galleryPositionLabel")}</strong>{" "}
              {t("content.blog.form.galleryPositionText")}
            </div>
            <Controller
              name="galleryImageIds"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="multiple"
                  folder="blog"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Author */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.blog.form.authorTitle")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="authorName" className="label">
                  {t("content.blog.form.authorNameLabel")}
                </label>
                <input
                  {...register("authorName")}
                  type="text"
                  id="authorName"
                  className="input"
                  placeholder={t("content.blog.form.authorNamePlaceholder")}
                />
              </div>
              <div>
                <label htmlFor="readTime" className="label">
                  {t("content.blog.form.readTimeLabel")}
                </label>
                <input
                  {...register("readTime", { valueAsNumber: true })}
                  type="number"
                  id="readTime"
                  className="input"
                  min={1}
                  placeholder={t("content.blog.form.readTimePlaceholder")}
                />
              </div>
              <div>
                <label className="label">{t("content.blog.form.authorAvatarLabel")}</label>
                <Controller
                  name="authorAvatarId"
                  control={control}
                  render={({ field }) => (
                    <MediaPicker
                      mode="single"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      folder="blog"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Related Trips */}
          {(allTrips.length > 0 || hiddenRelatedTripIds.length > 0) && (
            <div className="card">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("content.blog.form.relatedTripsTitle")}
                </h2>
                <p data-related-trips-hint className="mt-1 text-xs text-gray-500">
                  {t("content.blog.form.relatedTripsHint", {
                    count: relatedTripIds.length,
                    max: BLOG_RELATED_TRIPS_MAX,
                  })}
                </p>
                {atRelatedLimit && (
                  <p
                    data-related-trips-limit
                    className="mt-1 text-xs text-amber-700 dark:text-amber-400"
                  >
                    {hiddenRelatedTripIds.length > 0
                      ? t("content.blog.form.relatedTripsLimitReachedHidden", {
                          max: BLOG_RELATED_TRIPS_MAX,
                        })
                      : t("content.blog.form.relatedTripsLimitReached", {
                          max: BLOG_RELATED_TRIPS_MAX,
                        })}
                  </p>
                )}
                {hiddenRelatedTripIds.length > 0 && (
                  <div
                    data-related-trips-hidden
                    className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500"
                  >
                    <span>
                      {t("content.blog.form.relatedTripsHidden", {
                        count: hiddenRelatedTripIds.length,
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={removeHiddenRelatedTrips}
                      className="text-primary-600 hover:text-primary-700 font-medium underline"
                    >
                      {t("content.blog.form.relatedTripsRemoveHidden")}
                    </button>
                  </div>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {allTrips.map((trip) => {
                  const checked = relatedTripIds.includes(trip.id);
                  // Zaškrtnuté pole zakázané nikdy není — cesta zpět pod mez musí zůstat.
                  const disabled = atRelatedLimit && !checked;
                  return (
                    <label
                      key={trip.id}
                      className={`flex items-center gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700 ${
                        disabled
                          ? "cursor-not-allowed opacity-60"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleTripId(trip.id)}
                        className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{trip.title}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.blog.form.tagsTitle")}
            </h2>
            <textarea
              id="tags"
              rows={2}
              className="input"
              placeholder={t("content.blog.form.tagsPlaceholder")}
              value={watch("tags")?.join(", ") ?? ""}
              onChange={(e) => {
                const value = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                setValue("tags", value, { shouldDirty: true });
              }}
            />
            <p className="mt-1 text-xs text-gray-500">{t("content.blog.form.tagsHint")}</p>
          </div>

          {/* SEO */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.shared.seoTitle")}
            </h2>
            <div className="grid gap-4">
              <div>
                <label htmlFor="metaTitle" className="label">
                  {t("content.shared.metaTitleLabel")}
                </label>
                <input
                  {...register("metaTitle")}
                  type="text"
                  id="metaTitle"
                  className="input"
                  placeholder={t("content.shared.metaTitlePlaceholder")}
                  maxLength={60}
                />
              </div>
              <div>
                <label htmlFor="metaDescription" className="label">
                  {t("content.shared.metaDescriptionLabel")}
                </label>
                <textarea
                  {...register("metaDescription")}
                  id="metaDescription"
                  rows={2}
                  className="input"
                  placeholder={t("content.shared.metaDescriptionPlaceholder")}
                  maxLength={160}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.shared.featuredImageTitle")}
            </h2>
            <Controller
              name="featuredImageId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  purpose="hero"
                  mode="single"
                  folder="blog"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("common.settings")}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="status" className="label">
                  {t("common.fields.status")}
                </label>
                <select {...register("status")} id="status" className="input">
                  {BLOG_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {statusLabels[s] || s}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="publishedAt" className="label">
                  {t("content.blog.form.publishedAtLabel")}
                </label>
                <input
                  {...register("publishedAt")}
                  type="datetime-local"
                  id="publishedAt"
                  className="input"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("content.blog.form.publishedAtHint")}
                </p>
              </div>

              <div>
                <label htmlFor="sortOrder" className="label">
                  {t("common.fields.sortOrder")}
                </label>
                <input
                  {...register("sortOrder", { valueAsNumber: true })}
                  type="number"
                  id="sortOrder"
                  className="input"
                  min={0}
                />
                <p className="mt-1 text-xs text-gray-500">{t("content.shared.sortOrderHint")}</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  {...register("isFeatured")}
                  type="checkbox"
                  id="isFeatured"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isFeatured"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t("content.blog.form.featuredPost")}
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
